import moment from 'moment';
import { v4 } from 'uuid';

// Lodash
import capitalize from 'lodash/capitalize';

import { toast } from '@lavu-pos/lavu-react-components';
import { REPORT_DATE_FORMAT, XLSX_REPORT_DATE_FORMAT } from './constants/datetime';

export const randomId = () => v4();

export const centsToDollars = value => (parseInt(value, 10) / 100).toFixed(2);

/**
 * @typedef {Object} User - A user type.
 * @property {string} data.firstName - The user's first name
 * @property {string} data.lastName - The user's last name
 * @property {string} data.username - The user's username
 */

/**
 * Generate the username label on the system,
 * uses name and lastname abbreviation in ideal format
 * uses only last or first name if one is missing
 * falls back to username if both first and last name are missing
 *
 * @param {User} user - The user whose name we want to print
 *
 * @param {Object} options - The options for printing the user's name
 * @param {boolean} options.lastNameInitial - If it will show the last name's initial. It defaults to false.
 *
 * @returns {string} - The user's display name.
 *
 */
export const userDisplayNameParser = (
	user,
	{ lastNameInitial = false } = {}
) => {
	if (!user) {
		return '';
	}

	const { firstName, lastName, username } = user;
	const trimmedFirstName = firstName ? firstName.trim() : '';
	let trimmedLastName = '';
	if (lastName) {
		trimmedLastName = lastNameInitial
			? capitalize(lastName[0])
			: lastName.trim();
	}

	if (trimmedFirstName && trimmedLastName) {
		return `${trimmedFirstName} ${trimmedLastName}`;
	} else if (trimmedFirstName) {
		return trimmedFirstName;
	} else if (trimmedLastName) {
		return trimmedLastName;
	}

	return username;
};

/**
 * Function that, given 2 dates, returns true if the DateRangePicker renders
 * them as "Today".
 * DateRangePicker checks if startDate is "today" and endDate is "tomorrow".
 * If such condition is met, it displays the label "Today". When that happens,
 * we want the dateRangeReportParser to only show "startDate".
 *
 * @param {Object} startMoment - Moment Start Date
 * @param {Object} endMoment - Moment End Date
 *
 * @returns {boolean} if the date range will be formatted as "Today"
 */
const isToday = (startMoment, endMoment) =>
	startMoment &&
	endMoment &&
	moment().isSame(startMoment, 'day') &&
	moment()
		.add(1, 'day')
		.isSame(endMoment, 'day');

export const dateRangeReportParser = ({
	startDate,
	endDate,
	separator = ' ',
	dateFormat: userDateFormat,
}) => {
	const momentStartDate = moment(startDate);
	const momentEndDate = moment(endDate);
	const dateFormat = userDateFormat || REPORT_DATE_FORMAT(separator);
	const isRange =
		momentEndDate.diff(momentStartDate, 'days') > 0 &&
		!isToday(momentStartDate, momentEndDate);

	return `${momentStartDate.format(dateFormat)}${
		isRange ? ` - ${momentEndDate.format(dateFormat)}` : ''
	}`;
};

const notifyToast = ({
	kind = 'default',
	message = '',
	position = toast.POSITION.TOP_RIGHT,
	duration = 10000,
	dismissPrevious = true,
}) => {
	/* eslint no-unused-expressions: "off" */
	dismissPrevious && toast.dismiss();

	toast[kind](message, {
		autoClose: duration,
		position,
	});
};

notifyToast.POSITION = toast.POSITION;

export { notifyToast };

// validating email
export const emailIsValid = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
/**
 * @param  {Number} number - a number, if type of the provided element
 * is not a number then function will return undefined
 */

export const getOrdinal = n => {
	if (Number.isNaN(n)) {
		return undefined;
	}

	const s = ['th', 'st', 'nd', 'rd'];
	const v = n % 100;
	return n + (s[(v - 20) % 10] || s[v] || s[0]);
};
